import { useRef, useState } from "react";
import {
  Box, Typography, Paper, Button, Stack, Grid2 as Grid, TextField, Alert, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, IconButton, Divider, CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import SendIcon from "@mui/icons-material/Send";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  purchaseOrdersApi, purchaseOrderStateApi, branchesApi,
  type ItemDto, type VendorDto, type PurchaseRateDto,
  type CreatePurchaseOrderRequest,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { ItemPicker, VendorPicker } from "@/components/Pickers";
import { todayIso } from "@/shared/datetime";
import {
  resolvePurchaseRate, purchaseRateLabel, purchaseRateHint, isPurchaseRateUnreliable,
} from "@/shared/purchaseRates";

interface DraftLine {
  key: string;
  item: ItemDto | null;
  qty: number;
  rate: number;
  taxAmount: number;
  /** Where `rate` was defaulted from, for the caption under the field. Null once typed over. */
  rateSource: PurchaseRateDto | null;
  /** Set when the buyer types a rate, so re-resolving never overwrites their figure. */
  rateTouched: boolean;
}

const newLine = (): DraftLine => ({
  key: crypto.randomUUID(), item: null, qty: 1, rate: 0, taxAmount: 0,
  rateSource: null, rateTouched: false,
});

const taxOn = (qty: number, rate: number, gstRate?: number) =>
  Math.round(qty * rate * (gstRate ?? 0)) / 100;

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

export function CreatePurchaseOrderPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canSubmit = useAuth(s => s.hasPermission("procurement.po.submit"));

  const [vendor, setVendor] = useState<VendorDto | null>(null);
  const [warehouseId, setWarehouseId] = useState<string>("");
  const [documentDate, setDocumentDate] = useState<string>(todayIso);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState<string>("");
  const [vendorRef, setVendorRef] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [lines, setLines] = useState<DraftLine[]>([newLine()]);
  const [resolvingRate, setResolvingRate] = useState<Record<string, boolean>>({});
  /** Per-line request counter, so an out-of-order rate lookup cannot overwrite a newer one. */
  const rateReqSeq = useRef<Record<string, number>>({});

  const branches = useQuery({
    queryKey: ["branches"], queryFn: () => branchesApi.list(), staleTime: 5 * 60_000,
  });

  const updateLine = (key: string, patch: Partial<DraftLine>) =>
    setLines((ls) => ls.map((l) => {
      if (l.key !== key) return l;
      const next = { ...l, ...patch };
      // A typed rate is the buyer's own figure. Mark it so a later vendor change does not
      // re-resolve over it, and drop the caption, which no longer describes the number.
      if ("rate" in patch && !("rateSource" in patch)) {
        next.rateTouched = true;
        next.rateSource = null;
      }
      // A new item invalidates the old one's rate outright. The real one arrives from
      // fillRate() below; showing the previous item's figure in the meantime would be worse
      // than showing nothing.
      if ("item" in patch) {
        next.rate = 0;
        next.rateSource = null;
        next.rateTouched = false;
      }
      // Auto-calculate GST from the item's tax rate whenever item / qty / rate change.
      if ("item" in patch || "qty" in patch || "rate" in patch)
        next.taxAmount = taxOn(next.qty, next.rate, next.item?.gstRate);
      return next;
    }));

  /**
   * Default a line's rate from the vendor-item rate, then purchase history, then MRP — the same
   * precedence the reorder path has always used. Never overwrites a rate the buyer typed.
   */
  const fillRate = async (key: string, item: ItemDto | null, vendorId?: string) => {
    if (!item) return;
    // Picking an item and then switching vendor puts two lookups in flight for one line; without
    // a sequence the slower (older-vendor) answer can land last and win.
    const seq = (rateReqSeq.current[key] ?? 0) + 1;
    rateReqSeq.current[key] = seq;

    setResolvingRate((s) => ({ ...s, [key]: true }));
    const resolved = await resolvePurchaseRate(item.id, vendorId, item.defaultMrp);
    if (rateReqSeq.current[key] !== seq) return;   // superseded — leave the newer one to finish

    setResolvingRate((s) => { const n = { ...s }; delete n[key]; return n; });
    setLines((ls) => ls.map((l) => {
      // The line may have moved on while the lookup was in flight — another item picked, or a
      // rate typed. Either way the answer is stale and applying it would undo the buyer.
      if (l.key !== key || l.item?.id !== item.id || l.rateTouched) return l;
      return {
        ...l,
        rate: resolved.rate,
        rateSource: resolved,
        taxAmount: taxOn(l.qty, resolved.rate, l.item?.gstRate),
      };
    }));
  };

  /**
   * A different vendor is a different price list, so every rate this screen defaulted is re-asked.
   * Lines the buyer priced by hand are left alone.
   */
  const changeVendor = (v: VendorDto | null) => {
    setVendor(v);
    for (const l of lines) if (l.item && !l.rateTouched) void fillRate(l.key, l.item, v?.id);
  };

  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));
  const addLine = () => setLines((ls) => [...ls, newLine()]);

  const subTotal = lines.reduce((s, l) => s + l.qty * l.rate, 0);
  const taxTotal = lines.reduce((s, l) => s + l.taxAmount, 0);
  const grandTotal = subTotal + taxTotal;

  // Lines still carrying a retail MRP nobody confirmed. Deliberately a warning and not a
  // validation failure — a buyer may genuinely be paying list price — but this is the mistake
  // the old defaulting made silently, and it ends up in Batch.CostPrice via the GRN.
  const mrpDefaulted = lines
    .map((l, i) => ({ line: l, no: i + 1 }))
    .filter(({ line }) => line.item && !line.rateTouched
      && line.rateSource && isPurchaseRateUnreliable(line.rateSource.source));

  const validate = (): string | null => {
    if (!vendor) return "Pick a vendor";
    if (!warehouseId) return "Pick a destination warehouse";
    if (lines.length === 0) return "Add at least one line";
    for (const [i, l] of lines.entries()) {
      if (!l.item) return `Line ${i + 1}: item not selected`;
      if (l.qty <= 0) return `Line ${i + 1}: qty must be > 0`;
      if (l.rate < 0) return `Line ${i + 1}: rate must be >= 0`;
    }
    return null;
  };

  const mutation = useMutation({
    meta: { successMessage: "Purchase order created" },
    mutationFn: async (opts: { submit: boolean }) => {
      const payload: CreatePurchaseOrderRequest = {
        vendorId: vendor!.id,
        warehouseId,
        documentDate,
        expectedDeliveryDate: expectedDeliveryDate || undefined,
        vendorRef: vendorRef || undefined,
        notes: notes || undefined,
        lines: lines.map((l) => ({
          itemId: l.item!.id, qty: l.qty, rate: l.rate, taxAmount: l.taxAmount,
        })),
      };
      const created = await purchaseOrdersApi.create(payload);
      if (opts.submit) {
        // Fetch the fresh rowVersion, then submit with If-Match (S1 concurrency).
        const detail = await purchaseOrdersApi.get(created.id);
        await purchaseOrderStateApi.submit(created.id, detail.rowVersion);
      }
      return created;
    },
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ["purchase-orders"] });
      navigate(`/procurement/purchase-orders/${created.id}`);
    },
  });

  const validationError = validate();

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>New purchase order</Typography>
      <Paper sx={{ p: 3 }}>
        {mutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <VendorPicker value={vendor} onChange={changeVendor} label="Vendor" required />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField select SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} label="Destination warehouse *"
              value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}
              size="small" fullWidth>
              <option value="">— Pick branch —</option>
              {branches.data?.map((b) => <option key={b.id} value={b.id}>{b.code} — {b.name}</option>)}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Document date *" type="date" size="small" fullWidth
              value={documentDate} onChange={(e) => setDocumentDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Expected delivery" type="date" size="small" fullWidth
              value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)}
              InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <TextField label="Vendor reference" size="small" fullWidth
              value={vendorRef} onChange={(e) => setVendorRef(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField label="Notes" size="small" fullWidth multiline rows={1}
              value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle2">Lines</Typography>
          <Button startIcon={<AddIcon />} onClick={addLine} size="small">Add line</Button>
        </Stack>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 240 }}>Item *</TableCell>
              <TableCell align="right" sx={{ width: 100 }}>Qty *</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Rate (₹) *</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Tax (₹)</TableCell>
              <TableCell align="right" sx={{ width: 140 }}>Line total</TableCell>
              <TableCell sx={{ width: 50 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((l, idx) => {
              const lineTotal = l.qty * l.rate + l.taxAmount;
              return (
                <TableRow key={l.key}>
                  <TableCell>
                    <ItemPicker value={l.item}
                      onChange={(v) => { updateLine(l.key, { item: v }); void fillRate(l.key, v, vendor?.id); }}
                      label={`Line ${idx + 1}`} required error={!l.item} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.qty}
                      onChange={(e) => updateLine(l.key, { qty: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                      sx={{ width: 90 }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.rate}
                      onChange={(e) => updateLine(l.key, { rate: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.0001", style: { textAlign: "right" } }}
                      sx={{ width: 110 }} />
                    {resolvingRate[l.key] ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        resolving…
                      </Typography>
                    ) : l.rateSource && (
                      <Tooltip title={purchaseRateHint(l.rateSource)}>
                        <Typography variant="caption" display="block" sx={{ cursor: "help" }}
                          color={isPurchaseRateUnreliable(l.rateSource.source)
                            ? "warning.main" : "text.secondary"}
                          data-testid={`po-rate-source-${idx}`}>
                          {purchaseRateLabel(l.rateSource)}
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" size="small" value={l.taxAmount}
                      onChange={(e) => updateLine(l.key, { taxAmount: Number(e.target.value) || 0 })}
                      inputProps={{ min: 0, step: "0.01", style: { textAlign: "right" } }}
                      sx={{ width: 110 }} />
                  </TableCell>
                  <TableCell align="right">{inr.format(lineTotal)}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeLine(l.key)} disabled={lines.length === 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right"><strong>Subtotal</strong></TableCell>
              <TableCell align="right">{inr.format(subTotal)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right"><strong>Tax</strong></TableCell>
              <TableCell align="right">{inr.format(taxTotal)}</TableCell>
              <TableCell />
            </TableRow>
            <TableRow>
              <TableCell colSpan={3} />
              <TableCell align="right"><strong>Grand total</strong></TableCell>
              <TableCell align="right"><strong>{inr.format(grandTotal)}</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>

        {mrpDefaulted.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="po-mrp-default-warning">
            No purchase rate is on record for {mrpDefaulted.length === 1 ? "line" : "lines"}{" "}
            {mrpDefaulted.map(({ no }) => no).join(", ")}, so {mrpDefaulted.length === 1 ? "it is" : "they are"}{" "}
            showing the item&apos;s retail MRP. That is the selling price, not the vendor&apos;s —
            leaving it becomes the batch cost at GRN and values the stock at retail. Enter the
            vendor&apos;s rate, or set one under <b>Catalog → Vendor items</b>.
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button onClick={() => navigate("/procurement/purchase-orders")}>Cancel</Button>
          <Button variant="outlined"
            disabled={!!validationError || mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={() => mutation.mutate({ submit: false })}>
            Create draft
          </Button>
          {canSubmit && (
            <Button variant="contained" color="success"
              disabled={!!validationError || mutation.isPending}
              startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
              onClick={() => mutation.mutate({ submit: true })}>
              Create &amp; submit
            </Button>
          )}
        </Stack>
        {validationError && <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>}
      </Paper>
    </Box>
  );
}
